/**
 * Visual feedback for async button actions.
 * Shows a brief checkmark/X on the button text, then restores original.
 *
 * Usage:
 *   import { withFeedback } from '../lib/buttonFeedback.js';
 *   btn.addEventListener('click', () => withFeedback(btn, () => doAsyncThing()));
 */

const CHECKMARK = '\u2713'; // ✓
const CROSS = '\u2717';     // ✗
const SPINNER = '\u2022\u2022\u2022'; // •••

/**
 * Wrap an async action with button visual feedback.
 * - Immediately shows spinner dots and disables button
 * - On success: flashes green checkmark for 1.5s
 * - On error: flashes red X for 1.5s
 * - Restores original button text and state
 *
 * @param {HTMLButtonElement} btn - The button element
 * @param {Function} asyncFn - Async function to execute
 * @param {Object} [opts] - Options
 * @param {string} [opts.successText] - Custom success text (default: "✓ Done")
 * @param {string} [opts.errorText] - Custom error text (default: "✗ Failed")
 * @param {number} [opts.duration] - How long to show result (ms, default: 1500)
 */
export async function withFeedback(btn, asyncFn, opts = {}) {
    if (!btn || btn.disabled) return;

    const originalText = btn.textContent;
    const originalBg = btn.style.background;
    const originalColor = btn.style.color;
    const originalBorder = btn.style.borderColor;
    const duration = opts.duration || 1500;

    // Show working state
    btn.disabled = true;
    btn.textContent = SPINNER;
    btn.style.minWidth = btn.offsetWidth + 'px';

    try {
        await asyncFn();

        // Success flash
        btn.textContent = opts.successText || `${CHECKMARK} Done`;
        btn.style.background = 'var(--success, #16a34a)';
        btn.style.color = '#fff';
        btn.style.borderColor = 'var(--success, #16a34a)';
    } catch (err) {
        // Error flash
        btn.textContent = opts.errorText || `${CROSS} Failed`;
        btn.style.background = 'var(--error, #dc2626)';
        btn.style.color = '#fff';
        btn.style.borderColor = 'var(--error, #dc2626)';
    }

    // Restore after delay
    setTimeout(() => {
        btn.textContent = originalText;
        btn.style.background = originalBg;
        btn.style.color = originalColor;
        btn.style.borderColor = originalBorder;
        btn.style.minWidth = '';
        btn.disabled = false;
    }, duration);
}
